'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { useNotebook } from '@/lib/notebook/core/store'

function stableSerialize(value) {
  try {
    return JSON.stringify(value)
  } catch {
    return String(Date.now())
  }
}

/** Coalesce rapid patches into one parent write (reduces re-render flash while typing). */
const PARENT_EMIT_MS = 500

/**
 * Controlled block state synced with notebook store + PATCH persistence.
 *
 * Local edits win until the parent echoes the same payload back (pending-emit guard).
 * Parent onChange is never called during render or remote hydration.
 *
 * @param {Record<string, unknown>|undefined|null} data
 * @param {(next: Record<string, unknown>) => void|undefined} onChange
 * @param {{ defaults: Record<string, unknown> }} options
 */
export function usePersistedBlock(data, onChange, { defaults }) {
  const readOnly = useNotebook((s) => s.publicMode || s.locked)
  const defaultsRef = useRef(null)
  if (defaultsRef.current === null) defaultsRef.current = defaults

  const merge = useCallback((incoming) => {
    const base = defaultsRef.current ?? {}
    return {
      ...base,
      ...(incoming && typeof incoming === 'object' ? incoming : {}),
    }
  }, [])

  const [state, setState] = useState(() => merge(data))

  /** Snapshot we asked the parent to persist; block remote overwrites until echoed. */
  const pendingEmitKey = useRef(null)
  /** Last remote payload accepted into local state. */
  const acceptedRemoteKey = useRef(stableSerialize(merge(data)))
  /** Set by commit()/patch(); triggers debounced parent push. */
  const queuedEmitKey = useRef(null)
  const emitTimerRef = useRef(null)

  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange
  const stateRef = useRef(state)
  stateRef.current = state

  const flushEmit = useCallback(() => {
    if (emitTimerRef.current) {
      clearTimeout(emitTimerRef.current)
      emitTimerRef.current = null
    }
    if (!queuedEmitKey.current) return
    if (useNotebook.getState().publicMode || useNotebook.getState().locked) {
      queuedEmitKey.current = null
      return
    }

    const localKey = stableSerialize(stateRef.current)
    if (localKey !== queuedEmitKey.current) return

    queuedEmitKey.current = null
    pendingEmitKey.current = localKey
    onChangeRef.current?.(stateRef.current)
  }, [])

  // ── Remote → local (hydration, tab switch, server refresh) ─────────────────
  useEffect(() => {
    const incoming = merge(data)
    const incomingKey = stableSerialize(incoming)

    if (readOnly) {
      pendingEmitKey.current = null
      queuedEmitKey.current = null
      if (incomingKey === acceptedRemoteKey.current) return
      acceptedRemoteKey.current = incomingKey
      setState(incoming)
      return
    }

    if (pendingEmitKey.current) {
      if (incomingKey === pendingEmitKey.current) {
        pendingEmitKey.current = null
        acceptedRemoteKey.current = incomingKey
      }
      return
    }

    if (incomingKey === acceptedRemoteKey.current) return

    acceptedRemoteKey.current = incomingKey
    setState(incoming)
  }, [data, merge, readOnly])

  // ── Local → parent (debounced; flush on unmount) ───────────────────────────
  useEffect(() => {
    if (!queuedEmitKey.current) return

    const localKey = stableSerialize(state)
    if (localKey !== queuedEmitKey.current) return

    if (emitTimerRef.current) clearTimeout(emitTimerRef.current)
    emitTimerRef.current = setTimeout(flushEmit, PARENT_EMIT_MS)

    return () => {
      if (emitTimerRef.current) {
        clearTimeout(emitTimerRef.current)
        emitTimerRef.current = null
      }
    }
  }, [state, flushEmit])

  useEffect(() => () => flushEmit(), [flushEmit])

  const commit = useCallback((updater) => {
    if (useNotebook.getState().publicMode || useNotebook.getState().locked) {
      return
    }
    setState((prev) => {
      const next =
        typeof updater === 'function'
          ? /** @type {(p: Record<string, unknown>) => Record<string, unknown>} */ (
              updater
            )(prev)
          : updater
      queuedEmitKey.current = stableSerialize(next)
      return next
    })
  }, [])

  const patch = useCallback(
    (partial) => {
      commit((prev) => ({ ...prev, ...partial }))
    },
    [commit],
  )

  return { state, commit, patch }
}
